import { useState, useEffect, useCallback } from "react";
import { Box, Flex, Text, Button } from "@chakra-ui/react";
import apiClient from "Configuration/apiClient";
import API_CONFIG from "Configuration/apiConfig";
import AgGridWrapper from "components/AgGrid/AgGridWrapper";
import ExcelDownloadButton from "components/ExcelDownLoadButton/ExcelDownloadButton";
import GridPagination from "components/CustomPagination/GridPagination";
import { parseDate } from "Utils/dateUtils";
import {
  TABS, COLUMN_DEFS, PAGE_SIZE, exportToExcel,
} from "./disReportUtils";

export default function DisReportDp({ fromDate, toDate }) {
  const [activeTab,    setActiveTab]    = useState("general");
  const [generalTrade, setGeneralTrade] = useState({ auditedSize: null, sampleSize: null, uploadPercentage: null, data: [] });
  const [modernTrade,  setModernTrade]  = useState({ auditedSize: null, sampleSize: null, uploadPercentage: null, data: [] });
  const [loading,      setLoading]      = useState(false);
  const [exporting,    setExporting]    = useState(false);
  const [error,        setError]        = useState("");
  const [currentPage,  setCurrentPage]  = useState(0);

  const fetchReport = useCallback(async () => {
    if (!fromDate || !toDate) return;
    setLoading(true);
    setError("");
    try {
      const { year: fy, month: fm, day: fd } = parseDate(fromDate);
      const { year: ty, month: tm, day: td } = parseDate(toDate);
      const body = {
        from: { date: fd, month: fm, year: fy },
        to:   { date: td, month: tm, year: ty },
      };
      const url = API_CONFIG.ENDPOINTS.DIS_REPORT.DATA;

      const [generalRes, modernRes] = await Promise.all([
        apiClient.post(`${url}?scoreTypeId=1`, body),
        apiClient.post(`${url}?scoreTypeId=2`, body),
      ]);

      setGeneralTrade({
        auditedSize:      generalRes.data?.auditedSize      ?? null,
        sampleSize:       generalRes.data?.sampleSize       ?? null,
        uploadPercentage: generalRes.data?.uploadPercentage ?? null,
        data:             generalRes.data?.data             ?? [],
      });
      setModernTrade({
        auditedSize:      modernRes.data?.auditedSize      ?? null,
        sampleSize:       modernRes.data?.sampleSize       ?? null,
        uploadPercentage: modernRes.data?.uploadPercentage ?? null,
        data:             modernRes.data?.data             ?? [],
      });
      setCurrentPage(0);
    } catch {
      setError("Failed to load report data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    setCurrentPage(0);
  };

  const handleGoToPage = (page) => { // page is 1-based
    setCurrentPage(Math.min(Math.max(page - 1, 0), totalPages - 1));
  };

  const handleDownload = async () => {
    setExporting(true);
    try {
      await exportToExcel(generalTrade, modernTrade);
    } finally {
      setExporting(false);
    }
  };

  const activeData    = activeTab === "general" ? generalTrade : modernTrade;
  const activeTabMeta = TABS.find((t) => t.key === activeTab);
  const hasData       = generalTrade.data.length > 0 || modernTrade.data.length > 0;
  const totalPages    = Math.ceil(activeData.data.length / PAGE_SIZE);
  const pagedData     = activeData.data.slice(
    currentPage * PAGE_SIZE,
    (currentPage + 1) * PAGE_SIZE
  );

  return (
    <Box>
      {error && (
        <Text color="red.500" fontSize="sm" mb={4} fontWeight={500}>
          {error}
        </Text>
      )}

      {/* ── Tab buttons + Sample Size + Download ── */}
      <Flex
        bg="white"
        border="1px solid #EBEBEB"
        borderRadius="10px 10px 0 0"
        px={4}
        py={3}
        align="center"
        justify="space-between"
        boxShadow="0 1px 3px rgba(0,0,0,0.06)"
        flexWrap="wrap"
        gap={3}
      >
        {/* Tabs */}
        <Flex gap={3}>
          {TABS.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <Button
                key={tab.key}
                onClick={() => handleTabChange(tab.key)}
                px={7}
                py={2}
                h="38px"
                borderRadius="full"
                fontSize="sm"
                fontWeight={600}
                bg={isActive ? "#C0392B" : "gray.100"}
                color={isActive ? "white" : "gray.500"}
                border="1.5px solid"
                borderColor={isActive ? "#C0392B" : "gray.200"}
                boxShadow={isActive ? "0 2px 8px rgba(192,57,43,0.25)" : "none"}
                _hover={{
                  bg: isActive ? "#A93226" : "gray.200",
                  borderColor: isActive ? "#A93226" : "gray.300",
                  color: isActive ? "white" : "gray.600",
                }}
                transition="all 0.18s"
              >
                {tab.label}
              </Button>
            );
          })}
        </Flex>

        {/* Sample Size + Upload % + Download */}
        <Flex align="center" gap={4} flexWrap="wrap">
          <Flex align="center" gap={2}>
            <Text fontWeight={600} fontSize="md" color="gray.600">
              Total Collected Sample:
            </Text>
            <Box
              bg="yellow.300"
              px={4}
              py={1}
              borderRadius="6px"
              minW="70px"
              textAlign="center"
            >
              <Text fontWeight={700} fontSize="sm" color="gray.900">
               {activeData.auditedSize ?? "—"} / {activeData.sampleSize ?? "—"}
              </Text>
            </Box>
          </Flex>

          <Flex align="center" gap={2}>
            <Text fontWeight={600} fontSize="md" color="gray.600">
              Upload Percentage:
            </Text>
            <Box
              bg="green.100"
              px={4}
              py={1}
              borderRadius="6px"
              minW="70px"
              textAlign="center"
            >
              <Text fontWeight={700} fontSize="sm" color="gray.900">
                {activeData.uploadPercentage != null ? `${activeData.uploadPercentage}%` : "—"}
              </Text>
            </Box>
          </Flex>
        
          <ExcelDownloadButton
            onClick={handleDownload}
            loading={exporting}
            disabled={loading || !hasData}
          />
        </Flex>
      </Flex>

      {/* ── Grid panel ── */}
      <Box
        bg="white"
        border="1px solid #EBEBEB"
        borderTop="none"
        borderRadius="0 0 10px 10px"
        overflow="hidden"
        boxShadow="sm"
      >
        {/* Title banner */}
        <Box bg="#1F3864" px={4} py={3} textAlign="center">
          <Text as="span" color="white" fontWeight={700} fontSize="md" letterSpacing="0.3px">
            RED Project - Nepal - Daily Upload Status -{" "}
          </Text>
          <Text as="span" color="#FF4136" fontWeight={800} fontSize="md" letterSpacing="0.3px">
            {activeTabMeta.tradeLabel}
          </Text>
        </Box>

        <AgGridWrapper
          rowData={pagedData}
          columnDefs={COLUMN_DEFS}
          loading={loading}
          height="560px"
          noRowsMessage={
            !fromDate || !toDate
              ? "Select a date range to load data."
              : `No ${activeTabMeta.label} data found for the selected date range.`
          }
        />
      </Box>

      {/* ── Pagination ── */}
      {activeData.data.length > 0 && (
        <GridPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalElements={activeData.data.length}
          pageSize={PAGE_SIZE}
          onPrevious={() => setCurrentPage((p) => p - 1)}
          onNext={() => setCurrentPage((p) => p + 1)}
          onGoToPage={handleGoToPage}
        />
      )}
    </Box>
  );
}